import React from "react";
import { UserType, WorkOrderStatusType } from "../../utils/enum";
import useAuth from "../../hooks/useAuth";
import BaseTooltip from "../ui/BaseTooltip";

// Module-level so its identity is stable across renders — a component defined
// inside WorkOrderStatus would remount the badge (and its Tippy) on every render.
// Opens only once the pointer rests on the badge. On the boards the sub-status
// chip is ~16px tall, and with no delay a mouse passing along the row pops the
// tooltip in and out, which reads as flicker. Hide stays immediate.
const OfferTooltip = ({ enabled, content, children }) =>
  enabled ? (
    // Same paragraph wrapper the app's other tooltips use, so the text takes the
    // global 13px paragraph size instead of Tippy's 14px default.
    <BaseTooltip content={<p className="text-white">{content}</p>} delay={[250, 0]}>
      {children}
    </BaseTooltip>
  ) : (
    children
  );

const WorkOrderStatus = ({
  status,
  textSize = "text-xs",
  width = "w-20",
  className,
  isCustomer,
  // Off where the caller already wraps the badge in a tooltip of its own (the job
  // title card does): two nested Tippy instances on one element fight over
  // hover and the tooltip flickers.
  tooltip = true,
}) => {
  const { authRole } = useAuth();
  // Customers see a published work order as "Accepted" — from their side the client
  // accepted the ticket they submitted. Same status, different wording per audience.
  // Derived from the viewer unless a caller states it explicitly.
  const viewedByCustomer = isCustomer ?? authRole === UserType.CUSTOMER;

  const getStatus = () => {
    if (status === "PUBLISHED") {
      return (
        <p
          className={`${className} text-white ${width} bg-secondary text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          {viewedByCustomer ? "Accepted" : "Published"}
        </p>
      );
    } else if (status === "DRAFT") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#C0B023] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Drafted
        </p>
      );
    } else if (status === "PENDING_APPROVAL") {
      return (
        <p
          className={`${className} text-white whitespace-nowrap px-3 bg-[#4F46E5] text-center rounded-[5px] ${textSize} cursor-default`}
        >
          Submitted
        </p>
      );
    } else if (status === WorkOrderStatusType.COUNTER_OFFER_PENDING) {
      // The client sent the customer an offer that is awaiting an answer.
      return (
        <OfferTooltip
          enabled={tooltip}
          content={
            viewedByCustomer
              ? "Your service provider has sent you an offer for this work order. Accept or decline it to continue."
              : "Your offer has been sent to the customer and is waiting for their response."
          }
        >
          <p
            className={`${className} text-dark whitespace-nowrap px-3 bg-[#FFC107] text-center rounded-[5px] ${textSize} cursor-pointer`}
          >
            {viewedByCustomer ? "Offer Received" : "Offer Sent"}
          </p>
        </OfferTooltip>
      );
    } else if (status === WorkOrderStatusType.COUNTER_OFFER_ACCEPTED) {
      return (
        <OfferTooltip
          enabled={tooltip}
          content={
            viewedByCustomer
              ? "You accepted your service provider's offer for this work order."
              : "The customer accepted your offer for this work order."
          }
        >
          <p
            className={`${className} text-white whitespace-nowrap px-3 bg-[#268E2A] text-center rounded-[5px] ${textSize} cursor-pointer`}
          >
            Offer Accepted
          </p>
        </OfferTooltip>
      );
    } else if (status === WorkOrderStatusType.COUNTER_OFFER_DECLINED) {
      return (
        <OfferTooltip
          enabled={tooltip}
          content={
            viewedByCustomer
              ? "You declined your service provider's offer for this work order."
              : "The customer declined your offer for this work order. You can send a new one."
          }
        >
          <p
            className={`${className} text-white whitespace-nowrap px-3 bg-[#F44336] text-center rounded-[5px] ${textSize} cursor-pointer`}
          >
            Offer Declined
          </p>
        </OfferTooltip>
      );
    } else if (status === "REQUESTED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#9C27B0] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Requested
        </p>
      );
    } else if (status === "ROUTED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#2196F3] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Routed
        </p>
      );
    } else if (status === "COUNTER") {
      return (
        <p
          className={`${className} text-dark ${width} bg-[#FFC107] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Countered
        </p>
      );
    } else if (status === "DECLINED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#F44336] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Declined
        </p>
      );
    } else if (status === "REJECTED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#F44336] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Rejected
        </p>
      );
    } else if (status === "ASSIGNED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#2561A9] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Assigned
        </p>
      );
    } else if (status === "CONFIRM") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#5D7464] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Confirmed
        </p>
      );
    } else if (status === "NOT_CONFIRMED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#745b81] text-center  w-24 rounded-[5px] ${textSize} cursor-default`}
        >
          Not Confirmed
        </p>
      );
    } else if (status === "CONFIRMED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#71A534] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Confirmed
        </p>
      );
    } else if (status === "INPROGRESS") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#FF9800] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          In Progress
        </p>
      );
    } else if (status === "WORKDONE") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#295E80] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Work Done
        </p>
      );
    } else if (status === "INCOMPLETE") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#F44336] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Incomplete
        </p>
      );
    } else if (status === "CANCELLED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#F44336] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Cancelled
        </p>
      );
    } else if (status === "REVIEWED") {
      return (
        <p
          className={`${className} text-white ${width} bg-amber-600 text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Reviewed
        </p>
      );
    } else if (status === "VERIFIED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#279AA0] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Verified
        </p>
      );
    } else if (status === "APPROVED") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#268E2A] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Approved
        </p>
      );
    } else if (status === "PO_PENDING") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#FF69B4] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          PO Pending
        </p>
      );
    } else if (status === "PAID") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#0F40AE] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Paid
        </p>
      );
    } else if (status === "READY_TO_GO") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#02AABF] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Ready to go
        </p>
      );
    } else if (status === "CHECK_IN") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#0074A1] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Checked In
        </p>
      );
    } else if (status === "CHECK_OUT") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#673AB7] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Checked Out
        </p>
      );
    } else if (status === "ON_THE_WAY") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#2196F3] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          On the way
        </p>
      );
    } else if (status === "RUNNING_LATE") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#F44336] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Running Late
        </p>
      );
    } else if (status === "NOT_READY") {
      return (
        <p
          className={`${className} text-white ${width} bg-[#794CDA] text-center  rounded-[5px] ${textSize} cursor-default`}
        >
          Not Ready
        </p>
      );
    } else if (status === WorkOrderStatusType.INVOICED) {
      return (
        <p
          className={`${className}  ${width}  text-center  rounded-[5px] ${textSize} cursor-default bg-blue-600 text-white`}
        >
          Invoiced
        </p>
      );
    } else if (status === WorkOrderStatusType.DISPUTED) {
      return (
        <p
          className={`${className}  ${width}  text-center  rounded-[5px] ${textSize} cursor-default bg-amber-600 text-white`}
        >
          Disputed
        </p>
      );
    } else if (status === WorkOrderStatusType.CANCELLED_PAYABLE) {
      return (
        <p
          className={`${className}  ${width}  text-center  rounded-[5px] ${textSize} cursor-default bg-purple-600 text-white`}
        >
          Payable
        </p>
      );
    }
  };
  return getStatus();
};

export default WorkOrderStatus;
